/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
import { ScalableImage, Typography, useDevice, useWindowSize } from "design_system/src";
import { useSpring, animated, useSprings } from "@react-spring/web";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "./Brands.module.scss";

interface BrandItem {
  id: string;
  name: string;
  image: React.ReactElement;
  delay: number;
  duration: number;
  offset: number;
  rotation: number;
  bottom: number;
  zIndex: number;
}

const brandsList: {name: string, image: React.ReactElement}[] = [
  {
    name: "Vento",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_vento_blanco"
      alt="Vento – motos nuevas y seminuevas con Ozon"
    />,
  },
  {
    name: "Italika",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_Italika_blanco"
      alt="Italika – motocicletas a crédito con Ozon"
    />,
  },
  {
    name: "Victory",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_victory_blanco"
      alt="Victory – motos nuevas y seminuevas disponibles en Ozon"
    />,
  },
  {
    name: "Bajaj",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_bajaj_blanco"
      alt="Bajaj – motocicletas a crédito en Ozon
"
    />,
  },
];

const Brands: React.FC = () => {
  const { width: windowWidth } = useWindowSize();
  const { isMobile } = useDevice();
  const brandsSectionRef = useRef<HTMLDivElement>(null);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  const brandItems = useMemo((): BrandItem[] => {
    if (isMobile) {
      return [
        {
          id: "Victory-mobile-2",
          name: "Victory",
          image: brandsList[2].image,
          delay: 0.1,
          duration: 0.9,
          offset: 0,
          rotation: 20,
          bottom: 80,
          zIndex: 12,
        },
        {
          id: "Italika-mobile-1",
          name: "Italika",
          image: brandsList[1].image,
          delay: 0.1,
          duration: 0.9,
          offset: 0,
          rotation: -20,
          bottom: 57.5,
          zIndex: 11,
        },
        {
          id: "Vento-mobile-0",
          name: "Vento",
          image: brandsList[0].image,
          delay: 0.1,
          duration: 0.9,
          offset: 0,
          rotation: 20,
          bottom: 35,
          zIndex: 10,
        },
        {
          id: "Bajaj-mobile-2",
          name: "Bajaj",
          image: brandsList[3].image,
          delay: 0.1,
          duration: 0.9,
          offset: 0,
          rotation: -20,
          bottom: 12.5,
          zIndex: 13,
        },
      ];
    }

    return [
      {
        id: "Victory-desktop-0",
        name: "Victory",
        image: brandsList[2].image,
        delay: 0.1,
        duration: 0.9,
        offset: -windowWidth * 0.3,
        rotation: 20,
        bottom: 20,
        zIndex: 5,
      },
      {
        id: "Victory-desktop-1",
        name: "Victory",
        image: brandsList[2].image,
        delay: 0.1,
        duration: 0.9,
        offset: windowWidth * 0.25,
        rotation: -20,
        bottom: 10,
        zIndex: 15,
      },
      {
        id: "Victory-desktop-2",
        name: "Victory",
        image: brandsList[2].image,
        delay: 0.1,
        duration: 0.9,
        offset: -windowWidth * 0.25,
        rotation: 20,
        bottom: 85,
        zIndex: 15,
      },
      {
        id: "Victory-desktop-3",
        name: "Victory",
        image: brandsList[2].image,
        delay: 0.1,
        duration: 0.9,
        offset: windowWidth * 0.25,
        rotation: -20,
        bottom: 80,
        zIndex: 15,
      },

      {
        id: "Italika-desktop-0",
        name: "Italika",
        image: brandsList[1].image,
        delay: 0.1,
        duration: 0.9,
        offset: -windowWidth * 0.1,
        rotation: -10,
        bottom: 60,
        zIndex: 15,
      },
      {
        id: "Italika-desktop-1",
        name: "Italika",
        image: brandsList[1].image,
        delay: 0.1,
        duration: 0.9,
        offset: windowWidth * 0.15,
        rotation: -2,
        bottom: 35,
        zIndex: 5,
      },

      {
        id: "Vento-desktop-0",
        name: "Vento",
        image: brandsList[0].image,
        delay: 0.1,
        duration: 0.9,
        offset: -windowWidth * 0.3,
        rotation: -20,
        bottom: 65,
        zIndex: 15,
      },
      {
        id: "Vento-desktop-1",
        name: "Vento",
        image: brandsList[0].image,
        delay: 0.1,
        duration: 0.9,
        offset: windowWidth * 0.2,
        rotation: 10,
        bottom: 55,
        zIndex: 15,
      },

      {
        id: "Bajaj-desktop-1",
        name: "Bajaj",
        image: brandsList[3].image,
        delay: 0.1,
        duration: 0.9,
        offset: -windowWidth * 0.2,
        rotation: 10,
        bottom: 45,
        zIndex: 15,
      },
    ];

  }, [isMobile, windowWidth]);

  const limitTop = useMemo(() => isMobile || isTablet ? 0.35 : 0.4, [isMobile, isTablet]);
  const limitBottom = useMemo(() => isMobile || isTablet ? 1.05 : 1.1, [isMobile, isTablet]);

  const handleScroll = useCallback(() => {
    if (!brandsSectionRef.current) return;
    const rect = brandsSectionRef.current.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    const scrollBottom = window.scrollY + window.innerHeight;
    const containerHeight = window.innerHeight * 1.5;
    const containerBottom = top;
    const rawProgress = (scrollBottom - containerBottom) / containerHeight;
    const newProgress = Math.min(1.3, Math.max(0, rawProgress));
    setScrollProgress(newProgress);
  }, []);

  useEffect(() => {
    setIsTablet(windowWidth < 900);
  }, [windowWidth]);

  useEffect(() => {
    if (brandsSectionRef.current && isTablet !== undefined) {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isTablet, handleScroll]);

  const [brandSprings] = useSprings(
    brandItems.length,
    (index) => {
      const itemStartProgress = 0.05 + (index * 0.06);
      const itemEndProgress = itemStartProgress + 0.25;

      const itemProgress = Math.max(0, Math.min(1, 
        (scrollProgress - itemStartProgress) / (itemEndProgress - itemStartProgress)
      ));

      const startPosition = index < 3 ? 0 : window.innerHeight + 100;
      const endPosition = 0;

      return {
        transform: `translateY(${startPosition + (endPosition - startPosition) * itemProgress}px)`,
        config: {
          tension: 80,
          friction: 30,
          mass: 1,
        },
        immediate: false,
      };
    },
    [scrollProgress, brandItems]
  );

  const isTitleFixed = scrollProgress > limitTop && scrollProgress < limitBottom;
  const isTitleInitialState = scrollProgress > 0 && scrollProgress < limitTop;

  return (
    <section className="dso_container_desktop m_t_xxl">

      <div
        className={styles.brandsSection}
        ref={brandsSectionRef}
        style={{
          height: `${window.innerHeight * 1.5}px`,
          minHeight: "100vh"
        }}
      >
        {/* Title */}
        <div
          className={`${styles.stickyTitle} ${isTitleFixed ? styles.fixed : isTitleInitialState ? styles.initial : ""}`}
        >
          <Typography
            scale="heading1"
            scaleMobile="heading3"
            weight="600"
            component="h2"
            className="text_center p_x_md_mobile"
            style={{ zIndex: 12 }}
          >
            Las marcas más vendidas, <br className="display_none_mobile"/>ahora a tu alcance
          </Typography>
        </div>

        {/* Animated brand items */}
        <div className={styles.brandItemsContainer}>
          {brandSprings.map((spring, index) => {
            const item = brandItems[index];
            return (
              <animated.div
                key={item.id}
                style={{
                  ...spring,
                  left: `calc(50% + ${item.offset}px - ${250 / 2}px)`,
                  bottom: `${item.bottom}%`,
                  position: "absolute",
                  willChange: "transform",
                  transform: `rotate(${item.rotation}deg)`,
                  zIndex: item.zIndex,
                }}
                className={styles.brandItem}
              >
                <div 
                  className={styles.brandPill}
                >
                  {item.image}
                </div>
              </animated.div>
            );
          })}
        </div>
      </div>

    </section>
  );
};

export default Brands;
